import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerContext = createContext();

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('customer_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/customer/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data.customer);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/customer/register`, data);
      const { token: newToken, customer: customerData } = response.data;
      localStorage.setItem('customer_token', newToken);
      setToken(newToken);
      setCustomer(customerData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/customer/login`, {
        email,
        password
      });
      const { token: newToken, customer: customerData } = response.data;
      localStorage.setItem('customer_token', newToken);
      setToken(newToken);
      setCustomer(customerData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('customer_token');
    setToken(null);
    setCustomer(null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const refreshProfile = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/customer/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomer(response.data.customer);
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }
    }
  };

  const value = {
    customer,
    token,
    loading,
    isAuthenticated: !!customer,
    register,
    login,
    logout,
    getAuthHeaders,
    refreshProfile
  };

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
};
